import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { isTaskOverdue } from "../utils/taskHelpers";
import { format, parseISO, differenceInDays, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import { it } from "date-fns/locale";
import { Printer } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";

export default function ProjectGantt({ project, onSelectTask }) {
  const [tasks, setTasks] = useState([]);
  const [viewStart, setViewStart] = useState(startOfMonth(new Date()));
  const [orgInfo, setOrgInfo] = useState(null);

  useEffect(() => {
    if (!project?.organizationId) return; // aperta solo per id: aspetto i dati
    const q = query(collection(db, "tasks"), where("projectId", "==", project.id), where("organizationId","==",project.organizationId));
    return onSnapshot(q, snap => {
      const t = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.dueDate);
      setTasks(t);
    });
  }, [project.id, project.organizationId]);

  useEffect(() => {
    if (!project.organizationId) return;
    getDoc(doc(db,"organizations",project.organizationId)).then(s=>{ if(s.exists()) setOrgInfo(s.data()); });
  }, [project.organizationId]);

  const DAYS_VISIBLE = 30;
  const DAY_WIDTH = 32;
  const ROW_HEIGHT = 36;
  const LABEL_WIDTH = 220;

  const days = Array.from({ length: DAYS_VISIBLE }, (_, i) => addDays(viewStart, i));
  const today = toLocalDateStr(new Date());

  const getTaskBar = (task) => {
    if (!task.dueDate) return null;
    const due = parseISO(task.dueDate);
    const start = task.startDate ? parseISO(task.startDate) : due;
    const dayFromStart = differenceInDays(start, viewStart);
    const duration = Math.max(1, differenceInDays(due, start) + 1);
    const left = dayFromStart * DAY_WIDTH;
    const width = duration * DAY_WIDTH;
    return { left, width, inView: left + width > 0 && left < DAYS_VISIBLE * DAY_WIDTH };
  };

  const priorityColor = { alta: "#dc2626", media: "#d97706", bassa: "#059669" };

  // Stampa PDF — riproduce visivamente la griglia giorni/barre così com'è a schermo
  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const PDF_DAY_WIDTH = 22;

    const giorniHtml = days.map(d=>{
      const isWeekend = d.getDay()===0 || d.getDay()===6;
      return `<div style="width:${PDF_DAY_WIDTH}px;flex-shrink:0;text-align:center;font-size:8px;color:${isWeekend?"#9ca3af":"#374151"};border-right:1px solid #f3f4f6;padding:2px 0;">
        <div>${format(d,"d")}</div><div style="text-transform:uppercase;color:#9ca3af;">${format(d,"EEE",{locale:it})}</div>
      </div>`;
    }).join("");

    const righeHtml = tasks.map(task=>{
      const bar = getTaskBar(task);
      const isOverdue = isTaskOverdue(task);
      const barColor = task.status==="completata" ? "#059669" : isOverdue ? "#dc2626" : (priorityColor[task.priority]||"#7c3aed");
      const barHtml = (bar && bar.inView) ? `
        <div style="position:absolute;left:${Math.max(0,bar.left)*(PDF_DAY_WIDTH/DAY_WIDTH)}px;top:50%;transform:translateY(-50%);
          width:${Math.min(bar.width, DAYS_VISIBLE*DAY_WIDTH - Math.max(0,bar.left))*(PDF_DAY_WIDTH/DAY_WIDTH)}px;height:14px;border-radius:3px;
          background:${barColor};opacity:${task.status==="completata"?0.6:0.9};"></div>` : "";
      return `<div style="display:flex;height:24px;border-bottom:1px solid #f3f4f6;align-items:center;page-break-inside:avoid;">
        <div style="width:180px;flex-shrink:0;padding:0 10px;font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
          text-decoration:${task.status==="completata"?"line-through":"none"};color:${task.status==="completata"?"#9ca3af":"#111827"};border-right:1px solid #e5e7eb;">
          ${task.title||""}
        </div>
        <div style="flex:1;position:relative;height:100%;">${barHtml}</div>
      </div>`;
    }).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Gantt — ${project.name}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:10mm 12mm;}
      @media screen{body{max-width:1150px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 landscape;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;}
      .footer{border-top:1px solid #e5e7eb;margin-top:12px;padding-top:6px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>${logoHtml}
        <div style="font-size:11px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:15px;font-weight:800;color:#1e1b4b">GANTT — ${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||""}</div>
        <div style="font-size:11px;color:#6b7280">${format(viewStart,"MMMM yyyy",{locale:it})} · Stampato il ${new Date().toLocaleDateString("it-IT")}</div>
      </div>
    </div>
    <div style="display:flex;border-bottom:2px solid #e5e7eb;">
      <div style="width:180px;flex-shrink:0;padding:4px 10px;font-size:10px;font-weight:700;color:#6b7280;border-right:1px solid #e5e7eb;">Attività</div>
      <div style="flex:1;display:flex;">${giorniHtml}</div>
    </div>
    ${righeHtml || `<div style="padding:20px;text-align:center;color:#9ca3af;">Nessuna attività con scadenza nel progetto</div>`}
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Ad uso interno</span>
    </div>
    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
      <div style={{ padding: "16px 20px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1e1b4b" }}>📊 Gantt</span>
        <button onClick={stampaPDF}
          style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", border:"1px solid #2563eb", borderRadius:8, background:"#dbeafe", color:"#1d4ed8", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
          <Printer size={13}/> Esporta PDF
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setViewStart(addDays(viewStart, -7))}>← Prec.</button>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
          {format(viewStart, "MMMM yyyy", { locale: it })}
        </span>
        <button className="btn btn-secondary btn-sm" onClick={() => setViewStart(addDays(viewStart, 7))}>Succ. →</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setViewStart(startOfMonth(new Date()))}>Oggi</button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: LABEL_WIDTH + DAYS_VISIBLE * DAY_WIDTH }}>
          {/* Header giorni */}
          <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", position: "sticky", top: 0, background: "#fff", zIndex: 10 }}>
            <div style={{ width: LABEL_WIDTH, flexShrink: 0, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: "#6b7280", borderRight: "1px solid #e5e7eb" }}>
              Attività
            </div>
            {days.map(d => {
              const isToday = format(d, "yyyy-MM-dd") === today;
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              return (
                <div key={d.toString()} style={{ width: DAY_WIDTH, flexShrink: 0, textAlign: "center", padding: "4px 0", fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? "#7c3aed" : isWeekend ? "#9ca3af" : "#6b7280", background: isToday ? "#faf5ff" : "transparent", borderRight: "1px solid #f3f4f6" }}>
                  <div>{format(d, "d")}</div>
                  <div style={{ fontSize: 9, textTransform: "uppercase" }}>{format(d, "EEE", { locale: it })}</div>
                </div>
              );
            })}
          </div>

          {/* Righe attività */}
          {tasks.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
              <p>Nessuna attività con scadenza nel progetto</p>
            </div>
          ) : tasks.map(task => {
            const bar = getTaskBar(task);
            const isOverdue = isTaskOverdue(task);
            return (
              <div key={task.id} style={{ display: "flex", height: ROW_HEIGHT, borderBottom: "1px solid #f3f4f6", alignItems: "center" }}
                onMouseOver={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                {/* Label */}
                <div style={{ width: LABEL_WIDTH, flexShrink: 0, padding: "0 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 8, borderRight: "1px solid #e5e7eb", cursor: "pointer", overflow: "hidden" }}
                  onClick={() => onSelectTask(task)}>
                  <div className={`priority-dot ${task.priority || "media"}`} style={{ flexShrink: 0 }}/>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: task.status === "completata" ? "line-through" : "none", color: task.status === "completata" ? "#9ca3af" : "#111827" }}>
                    {task.title}
                  </span>
                </div>
                {/* Barra Gantt */}
                <div style={{ flex: 1, position: "relative", height: "100%", overflow: "hidden" }}>
                  {/* Sfondo weekend */}
                  {days.map((d, i) => {
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isToday = format(d, "yyyy-MM-dd") === today;
                    return (
                      <div key={i} style={{ position: "absolute", left: i * DAY_WIDTH, top: 0, width: DAY_WIDTH, height: "100%", background: isToday ? "rgba(124,58,237,0.04)" : isWeekend ? "#fafafa" : "transparent", borderRight: "1px solid #f9fafb" }}/>
                    );
                  })}
                  {/* Barra */}
                  {bar && bar.inView && (
                    <div style={{ position: "absolute", left: Math.max(0, bar.left), top: "50%", transform: "translateY(-50%)", width: Math.min(bar.width, DAYS_VISIBLE * DAY_WIDTH - Math.max(0, bar.left)), height: 20, borderRadius: 4, background: task.status === "completata" ? "#059669" : isOverdue ? "#dc2626" : priorityColor[task.priority] || "#7c3aed", opacity: task.status === "completata" ? 0.6 : 0.85, cursor: "pointer", display: "flex", alignItems: "center", paddingLeft: 6, overflow: "hidden" }}
                      onClick={() => onSelectTask(task)}>
                      <span style={{ fontSize: 11, color: "#fff", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{task.title}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
